import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Typography,
} from "@mui/material"
import { fetchWalletPayouts } from "../../../../../redux/specialist-slices/wallet.slice"
import { formatPrice } from "../../../../../utils/helper"

const STATUS_COLORS = {
  pending: { bg: "#fff8e1", color: "#f59e0b", label: "Pending" },
  paid: { bg: "#e8f5e9", color: "#22c55e", label: "Paid" },
  failed: { bg: "#fce4ec", color: "#ef4444", label: "Failed" },
  in_transit: { bg: "#e3f2fd", color: "#3b82f6", label: "In Transit" },
}

function PayoutsTab({ onboardingComplete }) {
  const dispatch = useDispatch()
  const { payouts, payoutsPagination, payoutsLoading, payoutsError } =
    useSelector((s) => s.walletSlice)

  const [page, setPage] = useState(1)

  // Only fetch when onboarding is complete — avoids a pointless API call
  useEffect(() => {
    if (!onboardingComplete) return
    dispatch(fetchWalletPayouts({ page, limit: 15 }))
  }, [page, dispatch, onboardingComplete])

  const totalPages = payoutsPagination?.totalPages || 1

  if (!onboardingComplete) {
    return (
      <Alert severity="warning" sx={{ borderRadius: "12px", fontSize: "13px" }}>
        Payouts are disabled until you complete Stripe onboarding. Please complete the
        onboarding steps above.
      </Alert>
    )
  }

  if (payoutsLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (payoutsError) {
    return (
      <Box sx={{ p: 3, bgcolor: "#fff5f5", borderRadius: "10px", color: "#b91c1c", fontSize: "13px" }}>
        {payoutsError}
      </Box>
    )
  }

  if (!payouts.length) {
    return (
      <Box
        sx={{
          py: 7,
          textAlign: "center",
          borderRadius: "12px",
          border: "1.5px dashed #ddd",
          color: "text.disabled",
        }}
      >
        <Typography fontSize="14px">No payouts yet. Complete sessions to earn!</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
        <Typography fontSize="12px" color="text.secondary">
          {payoutsPagination?.totalCount ?? 0} payout(s)
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {payouts.map((payout, i) => (
          <PayoutRow key={payout._id || i} payout={payout} />
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            sx={{ borderRadius: "8px", textTransform: "none", minWidth: 80 }}
          >
            Previous
          </Button>
          <Box
            sx={{
              px: 2,
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              color: "text.secondary",
            }}
          >
            Page {page} / {totalPages}
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            sx={{ borderRadius: "8px", textTransform: "none", minWidth: 80 }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}

function PayoutRow({ payout }) {
  const meta = STATUS_COLORS[payout.status] || {
    bg: "#f5f5f5",
    color: "#6b7280",
    label: payout.status,
  }

  const amount = formatPrice((payout.amount || 0) / 100, payout.currency || "inr")

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        borderRadius: "10px",
        border: "1px solid #efefef",
        bgcolor: "#fafafa",
        "&:hover": { bgcolor: "#f5f5f5" },
        transition: "background 0.15s",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor: meta.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            flexShrink: 0,
          }}
        >
          🏦
        </Box>
        <Box>
          <Typography fontWeight={600} fontSize="14px" color="other.color1">
            Stripe Payout
          </Typography>
          {payout.stripeTransferId && (
            <Typography fontSize="11px" color="text.disabled">
              {payout.stripeTransferId}
            </Typography>
          )}
          <Typography fontSize="11px" color="text.secondary">
            {payout.createdAt
              ? new Date(payout.createdAt).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Chip
          label={meta.label}
          size="small"
          sx={{
            bgcolor: meta.bg,
            color: meta.color,
            fontWeight: 600,
            fontSize: "11px",
            borderRadius: "6px",
            border: `1px solid ${meta.color}33`,
          }}
        />
        <Typography fontWeight={700} fontSize="15px" color="#22c55e">
          {amount}
        </Typography>
      </Box>
    </Box>
  )
}

export default PayoutsTab
